import { t } from "./i18n";

export type QuantityControlKind = "card" | "dialog" | "catalog" | "variant";
export interface QuantityControlView {
  key: string;
  title: string;
  quantity: number;
  kind: QuantityControlKind;
  expanded?: boolean;
}

function escape(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function controlState(view: QuantityControlView) {
  const quantity = Math.min(99, Math.max(0, Math.trunc(view.quantity) || 0));
  const compact = view.kind === "catalog" && !view.expanded && quantity > 0;
  return {
    quantity, compact,
    label: t("shoppingQuantityForProduct", { title: view.title }),
    add: t("addProductToList", { title: view.title }),
    decrease: t(quantity === 1 ? "removeProductFromList" : "decreaseQuantity", { title: view.title }),
    increase: t("increaseQuantity", { title: view.title }),
    edit: t("editShoppingQuantity", { title: view.title, quantity }),
    value: t("quantityValue", { quantity }),
  };
}

/** No nested buttons. All nodes survive quantity changes (focus and fast taps). */
export function productQuantityControlMarkup(view: QuantityControlView): string {
  const state = controlState(view);
  return `<div class="product-quantity-control quantity-${view.kind}${state.quantity ? " is-added" : ""}${state.compact ? " is-compact" : ""}"
    data-quantity-key="${escape(view.key)}" data-quantity-kind="${view.kind}"
    role="group" aria-label="${escape(state.label)}">
    <button type="button" class="quantity-add" data-quantity-action="add"
      aria-label="${escape(state.add)}" ${state.quantity ? "hidden" : ""}>
      <span aria-hidden="true">+</span>${view.kind === "dialog" ? `<span class="quantity-add-label">${escape(t("addToShoppingList"))}</span>` : ""}
    </button>
    <button type="button" class="quantity-compact" data-quantity-action="expand"
      aria-label="${escape(state.edit)}" ${state.compact ? "" : "hidden"}>
      <span class="quantity-check" aria-hidden="true">✓</span><span data-quantity-compact-value aria-hidden="true">${state.quantity}</span>
    </button>
    <div class="quantity-stepper" ${state.quantity && !state.compact ? "" : "hidden"}>
      <button type="button" data-quantity-action="decrease" aria-label="${escape(state.decrease)}">−</button>
      <span class="quantity-value" role="status" aria-live="polite" aria-atomic="true" aria-label="${escape(state.value)}">
        <span class="quantity-check" aria-hidden="true">✓</span><span data-quantity-value aria-hidden="true">${state.quantity}</span>
      </span>
      <button type="button" data-quantity-action="increase" aria-label="${escape(state.increase)}" ${state.quantity >= 99 ? "disabled" : ""}>+</button>
    </div>
  </div>`;
}

export function updateProductQuantityControl(element: HTMLElement, view: QuantityControlView): void {
  const state = controlState(view);
  const add = element.querySelector<HTMLButtonElement>(".quantity-add")!;
  const compact = element.querySelector<HTMLButtonElement>(".quantity-compact")!;
  const stepper = element.querySelector<HTMLElement>(".quantity-stepper")!;
  const decrease = element.querySelector<HTMLButtonElement>('[data-quantity-action="decrease"]')!;
  const increase = element.querySelector<HTMLButtonElement>('[data-quantity-action="increase"]')!;
  const previousFocus = element.contains(document.activeElement) ? document.activeElement : null;
  element.dataset.quantityKey = view.key;
  element.classList.toggle("is-added", state.quantity > 0);
  element.classList.toggle("is-compact", state.compact);
  element.setAttribute("aria-label", state.label);
  add.hidden = state.quantity > 0;
  add.setAttribute("aria-label", state.add);
  const addLabel = add.querySelector<HTMLElement>(".quantity-add-label");
  if (addLabel) addLabel.textContent = t("addToShoppingList");
  compact.hidden = !state.compact;
  compact.setAttribute("aria-label", state.edit);
  stepper.hidden = !state.quantity || state.compact;
  decrease.setAttribute("aria-label", state.decrease);
  increase.setAttribute("aria-label", state.increase);
  increase.disabled = state.quantity >= 99;
  const value = element.querySelector<HTMLElement>(".quantity-value")!;
  if (value.getAttribute("aria-label") !== state.value) value.setAttribute("aria-label", state.value);
  element.querySelectorAll<HTMLElement>("[data-quantity-value], [data-quantity-compact-value]").forEach((node) => {
    if (node.textContent !== String(state.quantity)) node.textContent = String(state.quantity);
  });
  if (previousFocus && (previousFocus.closest("[hidden]") || previousFocus === increase && increase.disabled)) {
    (state.compact ? compact : !state.quantity ? add : state.quantity < 99 ? increase : decrease)
      .focus({ preventScroll: true });
  }
}

/** The expanded catalog control must not extend past the native page boundary. */
export function clampQuantityControlAnchor(anchor: number, container: number, control: number): number {
  if (container <= 0 || control >= container) return 50;
  const inset = Math.min(50, (control / 2 + 2) / container * 100);
  return Math.min(100 - inset, Math.max(inset, anchor));
}
